import React from 'react';

import { Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { SystemOrderStatus } from '@app/models/orders';
import { Module } from '@app/models/users';
import { formatDate } from '@app/services/date';
import { FilterBlock, FilterInput, IField, IInputFilterValue } from '@components/FilterBlock';

import {
  ICustomerFilterValue,
  IDateRangeValue,
  IOrdersFilterValues,
  IStatusFilterValue,
  toOrdersFilters,
} from '../../data/filters';
import { IOrdersFilters, OrdersTab, WAREHOUSE_TAB_STATUSES } from '../../interfaces';

import {
  CustomerFilterSelect,
  CustomerTitle,
  DateRangeFilter,
  IStatusOption,
  StatusFilterSelect,
} from './FilterFields';

interface IProps {
  tab: OrdersTab;
  total?: number;
  // restored - блок сам отдал сохранённые значения, пользователь фильтры не менял
  onChange: (filters: IOrdersFilters, restored: boolean) => void;
}

export const OrdersFilters: React.FC<IProps> = ({ tab, total, onChange }) => {
  const ordersT = useTranslation(NSKeys.counterpartyOrders);
  // Сохранённые значения блок отдаёт при открытии, и его эффект срабатывает раньше этого
  const isOpened = React.useRef(false);

  React.useEffect(() => {
    isOpened.current = true;
    return () => {
      isOpened.current = false;
    };
  }, []);

  const fields = React.useMemo<IField[]>(() => {
    // Во вкладке «Склады» в колонке складской статус, в остальных - статус заказа
    const statusOptions: IStatusOption[] =
      tab === Module.Warehouse
        ? WAREHOUSE_TAB_STATUSES.map((status) => ({ value: status, label: ordersT.t(`status.${status}` as const) }))
        : Object.values(SystemOrderStatus).map((status) => ({
            value: status,
            label: ordersT.t(`orderStatus.${status}` as const),
          }));
    const renderText = (value: IInputFilterValue) => `${value?.value ?? ''}`;
    const renderDates = ({ from, to }: IDateRangeValue) => {
      if (from && to) {
        return `${formatDate(from)} – ${formatDate(to)}`;
      }
      return from ? `${ordersT.t('filters.from')} ${formatDate(from)}` : `${ordersT.t('filters.to')} ${formatDate(to)}`;
    };

    return [
      {
        label: ordersT.t('filters.customer'),
        name: 'customer_scope_id',
        inputField: <CustomerFilterSelect />,
        renderDisplayValue: (value: ICustomerFilterValue) => <CustomerTitle id={value?.value} />,
      },
      {
        label: ordersT.t('filters.recipient'),
        name: 'recipient',
        inputField: <FilterInput />,
        renderDisplayValue: renderText,
      },
      {
        label: ordersT.t('filters.city'),
        name: 'delivery_city',
        inputField: <FilterInput />,
        renderDisplayValue: renderText,
      },
      {
        label: ordersT.t('filters.address'),
        name: 'delivery_address',
        inputField: <FilterInput />,
        renderDisplayValue: renderText,
      },
      {
        label: ordersT.t('filters.createdAt'),
        name: 'created',
        inputField: <DateRangeFilter />,
        renderDisplayValue: renderDates,
      },
      {
        label: ordersT.t('filters.deliveryDate'),
        name: 'delivery_date',
        inputField: <DateRangeFilter />,
        renderDisplayValue: renderDates,
      },
      {
        label: ordersT.t('filters.status'),
        name: 'status',
        inputField: <StatusFilterSelect options={statusOptions} />,
        renderDisplayValue: (value: IStatusFilterValue) =>
          (value?.value || [])
            .map((status) => statusOptions.find((option) => option.value === status)?.label ?? status)
            .join(', '),
      },
    ];
  }, [tab, ordersT.t]);

  return (
    <Flex justify="space-between" align="center" gap={16}>
      <FilterBlock
        uidPrefix={`counterparty-orders-${tab}`}
        fields={fields}
        showSearchField={false}
        requiredSearchField={false}
        onSubmitValues={(values: IOrdersFilterValues) => onChange(toOrdersFilters(values), !isOpened.current)}
        initialValues={{}}
      />
      {total !== undefined && (
        <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
          {ordersT.t('filters.total', { count: total })}
        </Typography.Text>
      )}
    </Flex>
  );
};
